import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, useToast, type BadgeTone } from '@artosera/ui';
import { demanderExport, demanderTestRestauration, renvoyerExport } from '../../lib/api.js';
import { usePeut } from '../../auth/usePeut.js';
import { formaterDate, formaterOctets } from '../../lib/format.js';
import type { EtatExport, EtatSauvegarde, ExportGalerie, GalerieFiche } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import { TableauDefilant } from '../../components/TableauDefilant.js';
import { useElevation } from '../../components/useElevation.js';
import tableStyles from '../../styles/table.module.css';

const TONE_SAUVEGARDE: Record<EtatSauvegarde, BadgeTone> = {
  reussie: 'available',
  echouee: 'sold',
  en_cours: 'reserved',
};

/** Le badge d'un export dit ce qu'il y a dans le stockage, pas le travail. */
const TONE_EXPORT: Record<EtatExport, BadgeTone> = {
  en_attente: 'neutral',
  en_cours: 'reserved',
  verification: 'reserved',
  pret: 'available',
  incomplet: 'reserved',
  echoue: 'sold',
  manquant: 'sold',
  supprime: 'withdrawn',
};

/** Les seize premiers caractères d'une empreinte : de quoi la comparer à l'œil. */
function empreinteCourte(sha256: string | null): string {
  return sha256 === null ? '—' : `${sha256.slice(0, 16)}…`;
}

function lienExpire(exportItem: ExportGalerie): boolean {
  return exportItem.expireLe !== null && new Date(exportItem.expireLe).getTime() <= Date.now();
}

export function SauvegardesTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const peutGerer = peut('console:manage-backups');
  const peutExporter = peut('console:export-gallery');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // L'export complet est toutes les données de la galerie : second facteur
  // récent, comme la résiliation (revue du 9 octobre, n° 9).
  const elevation = useElevation();

  const invalider = (): Promise<void> =>
    queryClient
      .invalidateQueries({ queryKey: ['console', 'travaux'] })
      .then(() => queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] }))
      .then(() => undefined);

  const testRestauration = useMutation({
    mutationFn: () => demanderTestRestauration(idGalerie),
    onSuccess: async () => {
      toast({ title: t('fiche.sauvegardes.succesTest'), closeLabel: t('fiche.domaines.fermer') });
      await invalider();
    },
  });

  const renvoi = useMutation({
    mutationFn: (idExport: string) => renvoyerExport(idGalerie, idExport),
    onSuccess: async (reponse) => {
      toast({
        title: t('fiche.sauvegardes.renvoye', {
          transmis: reponse.transmis,
          date: formaterDate(reponse.expireLe, '—'),
        }),
        closeLabel: t('fiche.domaines.fermer'),
      });
      await invalider();
    },
  });

  const exportComplet = useMutation({
    mutationFn: () => elevation.executer(() => demanderExport(idGalerie)),
    onSuccess: async () => {
      toast({ title: t('fiche.sauvegardes.succesExport'), closeLabel: t('fiche.domaines.fermer') });
      await invalider();
    },
  });

  return (
    <div>
      <div className={pageStyles.sectionTitre}>
        <h2>{t('fiche.sauvegardes.titre')}</h2>
        {peutGerer ? (
          <Button
            variant="secondary"
            size="sm"
            loading={testRestauration.isPending}
            onClick={() => testRestauration.mutate()}
          >
            {testRestauration.isPending
              ? t('fiche.sauvegardes.enCoursTest')
              : t('fiche.sauvegardes.lancerTest')}
          </Button>
        ) : null}
      </div>

      {galerie.sauvegardes.length === 0 ? (
        <p className={pageStyles.videEtat}>{t('fiche.sauvegardes.vide')}</p>
      ) : (
        <TableauDefilant>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('fiche.sauvegardes.date')}</th>
                <th scope="col">{t('fiche.sauvegardes.taille')}</th>
                <th scope="col">{t('fiche.sauvegardes.etat')}</th>
              </tr>
            </thead>
            <tbody>
              {galerie.sauvegardes.map((sauvegarde) => (
                <tr key={sauvegarde.date}>
                  <td className={tableStyles.cellulePrincipale}>
                    {formaterDate(sauvegarde.date, '—')}
                  </td>
                  <td>{formaterOctets(sauvegarde.taille)}</td>
                  <td>
                    <Badge tone={TONE_SAUVEGARDE[sauvegarde.etat]}>
                      {t(`fiche.sauvegardes.etatValeur.${sauvegarde.etat}`)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableauDefilant>
      )}

      <div className={pageStyles.sectionTitre}>
        <h2>{t('fiche.sauvegardes.exportsTitre')}</h2>
        {peutExporter ? (
          <Button
            variant="secondary"
            size="sm"
            loading={exportComplet.isPending}
            onClick={() => exportComplet.mutate()}
          >
            {exportComplet.isPending
              ? t('fiche.sauvegardes.enCoursExport')
              : t('fiche.sauvegardes.demander')}
          </Button>
        ) : null}
      </div>

      <p className={tableStyles.celluleMuette}>{t('fiche.sauvegardes.consoleSansAcces')}</p>

      {galerie.exports.length === 0 ? (
        <p className={pageStyles.videEtat}>{t('fiche.sauvegardes.videExports')}</p>
      ) : (
        <TableauDefilant>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('fiche.sauvegardes.demandeLe')}</th>
                <th scope="col">{t('fiche.sauvegardes.exportEtat')}</th>
                <th scope="col">{t('fiche.sauvegardes.exportTaille')}</th>
                <th scope="col">{t('fiche.sauvegardes.pretLe')}</th>
                <th scope="col">{t('fiche.sauvegardes.expireLe')}</th>
                <th scope="col">{t('fiche.sauvegardes.exportEmpreinte')}</th>
                {peutExporter ? <th scope="col">{t('fiche.sauvegardes.exportActions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {galerie.exports.map((exportItem) => (
                <tr key={exportItem.jobId}>
                  <td className={tableStyles.cellulePrincipale}>
                    {formaterDate(exportItem.demandeLe, '—')}
                    <br />
                    <span className={tableStyles.celluleMuette}>
                      {t(`fiche.sauvegardes.exportOrigine.${exportItem.origine}`)}
                      {exportItem.final ? ` · ${t('fiche.sauvegardes.exportFinal')}` : ''}
                    </span>
                  </td>
                  <td>
                    <Badge tone={TONE_EXPORT[exportItem.etat]}>
                      {t(`fiche.sauvegardes.exportEtatValeur.${exportItem.etat}`)}
                    </Badge>
                    {exportItem.etat === 'verification' ? (
                      <>
                        <br />
                        <span className={tableStyles.celluleMuette}>
                          {t('fiche.sauvegardes.verificationAide')}
                        </span>
                      </>
                    ) : null}
                    {exportItem.erreur !== null ? (
                      <>
                        <br />
                        <span className={tableStyles.celluleMuette}>{exportItem.erreur}</span>
                      </>
                    ) : null}
                  </td>
                  <td>{exportItem.octets === null ? '—' : formaterOctets(exportItem.octets)}</td>
                  <td>{formaterDate(exportItem.pretLe, '—')}</td>
                  <td>
                    {lienExpire(exportItem)
                      ? t('fiche.sauvegardes.lienExpire')
                      : formaterDate(exportItem.expireLe, '—')}
                    {exportItem.accuseLe !== null ? (
                      <>
                        <br />
                        <span className={tableStyles.celluleMuette}>
                          {t('fiche.sauvegardes.accuseLe', {
                            date: formaterDate(exportItem.accuseLe, '—'),
                          })}
                        </span>
                      </>
                    ) : exportItem.telechargeLe !== null ? (
                      <>
                        <br />
                        <span className={tableStyles.celluleMuette}>
                          {t('fiche.sauvegardes.telechargeLe', {
                            date: formaterDate(exportItem.telechargeLe, '—'),
                          })}
                        </span>
                      </>
                    ) : exportItem.envoyeLe !== null ? (
                      <>
                        <br />
                        <span className={tableStyles.celluleMuette}>
                          {t('fiche.sauvegardes.envoyeLe', {
                            date: formaterDate(exportItem.envoyeLe, '—'),
                          })}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <code title={exportItem.sha256 ?? undefined}>
                      {empreinteCourte(exportItem.sha256)}
                    </code>
                  </td>
                  {peutExporter ? (
                    <td className={tableStyles.celluleActions}>
                      {exportItem.etat === 'pret' && exportItem.id !== null ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={renvoi.isPending && renvoi.variables === exportItem.id}
                          onClick={() => {
                            if (exportItem.id !== null) renvoi.mutate(exportItem.id);
                          }}
                        >
                          {t('fiche.sauvegardes.renvoyer')}
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </TableauDefilant>
      )}
      {elevation.dialogue}
    </div>
  );
}
